import { reportError } from 'config/sentry';
import { createContext } from 'react';
import queryString from 'query-string';
import { action, computed, observable } from 'mobx';
import merge from 'deepmerge';
import { DateTime } from 'luxon';
import {
  getCourseInfo as getDynamicCourseInfo,
  getCourseInfoMeta,
} from 'services/ClassroomService';
import CatalogStore from 'stores/Catalog';
import {
  getCoursePage,
  getCourseStructure,
  getCourseOffering,
  createUpdateCourseBookmark,
  getCourseMetadata,
} from 'services/CourseService';
import { postEvent } from 'services/EventService';
import {
  getCourseEnrollment,
  getEnrollmentInstance,
  saveEnrollmentUserData,
  getUserEnrollmentInfo,
  saveUserEnrollmentInfo,
} from 'services/UserEnrollmentService';
import { ZDOC_COURSE_INFO } from 'config/constants';
import ZDocManagerStore from 'stores/ZdocManager';
import {
  CourseFormat,
  CourseType,
  Modality,
  UserEnrollmentInfo,
  OfferingTier,
  CatalogEntry,
} from 'types';
import reverse from 'lodash/reverse';
import UIStore from 'stores/UI';
import UserStore from 'stores/User';
import ProgressStore from 'stores/Progress';
import ClassesStore from 'stores/Classes';
import EventStore from 'stores/Event';
import ConfigStore from 'stores/Config';
import FeaturesStore from 'stores/Features';
import { EnablementFeatureFlags } from 'config/featureFlags';
import {
  CourseHeaderSummary,
  CourseInfo,
  CourseStructureItem,
  DynamicCourseInfo,
} from 'types/course';
import { ProgressByCourse, UserProgressPage } from 'types/progress';
import { getLabGradeStatusByCourseSlugAndPageSlug } from 'services/LabGradeResultsService';
import { LabGradeStatus } from 'types/labs';

const dateToTimestamp = (date) => {
  const dateObj = date ? DateTime.fromISO(date) : DateTime.now();
  return dateObj.toISO();
};

const allowedPreviousRoutes = ['catalog', 'home'];

type CourseDynamicInfo = { dynamic_info: DynamicCourseInfo };

class CourseStore {
  @observable isLoadingInitialData = true;

  @observable loadedInitialData = false;

  @observable initialLoadError = false;

  @observable initialErrorCount = 0; // number of times the initial data fetch has failed. Useful for UI.

  @observable isLoadingComplementaryData = true;

  @observable loadedComplementaryData = false;

  @observable complementaryDataLoadError = false;

  @observable isLoadingPage = true;

  @observable loadedPage = false;

  @observable pageFetchError = false;

  // base course info:
  @observable courseSlug?: string;

  @observable dynamicCourseInfo?: CourseDynamicInfo;

  @observable courseInfoMeta?: CourseInfo;

  @observable courseOffering?; // contains expert extras and lab plus list

  @observable courseCode?: string;

  @observable courseVersion?: string;

  @observable courseCatalogEntry?: CatalogEntry;

  @observable courseMetadata?;

  @observable translations: string[] = [];

  @observable versions: string[] = [];

  // enrollment info:
  @observable euuid?;

  @observable enrollment?;

  @observable courseProgress?: ProgressByCourse;

  @observable userProgressPage?: UserProgressPage;

  @observable courseProgressLoaded = false;

  @observable userEnrollmentInfo: UserEnrollmentInfo = {} as UserEnrollmentInfo;

  // complementary data based on language:
  @observable courseStructure?: CourseStructureItem[];

  @observable pageSlug?: string;

  @observable page?; // page content

  // other settings
  @observable languageOverride: string;

  @observable offeringSettings = {};

  @observable originRouteName = 'catalog';

  @observable isSyndicationEnabled = false;

  @observable currentlabGradeStatus?: LabGradeStatus;

  routerStore;

  uiStore: UIStore;

  userStore: UserStore;

  catalogStore: CatalogStore;

  progressStore: ProgressStore;

  classesStore: ClassesStore;

  localLabStore: any;

  eventStore: EventStore;

  configStore: ConfigStore;

  featuresStore: FeaturesStore;

  zdocManagerStore: ZDocManagerStore;

  constructor(
    routerStore: any,
    uiStore: UIStore,
    userStore: UserStore,
    catalogStore: CatalogStore,
    progressStore: ProgressStore,
    classesStore: ClassesStore,
    localLabStore: any,
    eventStore: EventStore,
    configStore: ConfigStore,
    featuresStore: FeaturesStore,
  ) {
    this.routerStore = routerStore;
    this.uiStore = uiStore;
    this.userStore = userStore;
    this.catalogStore = catalogStore;
    this.progressStore = progressStore;
    this.classesStore = classesStore;
    this.localLabStore = localLabStore;
    this.eventStore = eventStore;
    this.configStore = configStore;
    this.featuresStore = featuresStore;

    this.zdocManagerStore = new ZDocManagerStore(ZDOC_COURSE_INFO);

    this.pageSlug = this.routerStore?.route?.params?.page;
  }

  @computed get language(): string {
    return (
      this.languageOverride ||
      this.routerStore?.route?.params?.language ||
      this.userData?.language ||
      this.uiStore.currentLanguage ||
      'en-US'
    );
  }

  static getLastPageAccessed(
    userEnrollmentInfo: UserEnrollmentInfo,
    structure: CourseStructureItem[],
  ) {
    return (
      userEnrollmentInfo?.last_page_accessed || structure[0]?.page_tag || 'ch01'
    );
  }

  @computed get lastPageAccessed() {
    return CourseStore.getLastPageAccessed(
      this.userEnrollmentInfo,
      this.courseStructure,
    );
  }

  /**
   * Returns the course info for the most current version of the course.
   */
  static getCurrentCourse(
    courseInfo: CourseDynamicInfo,
    language = 'en-US',
  ): CourseHeaderSummary {
    const { dynamic_info: dynamicInfo } = courseInfo || {};

    const found = dynamicInfo?.summaries.find(
      (course) =>
        course.version === dynamicInfo.current && course.language === language,
    );

    const defaultCourse = dynamicInfo?.summaries.find(
      (course) =>
        course.version === dynamicInfo.current && course.language === 'en-US',
    );

    return found || defaultCourse;
  }

  static isBundle(catalogEntry?: CatalogEntry) {
    return catalogEntry?.format === CourseFormat.Bundle;
  }

  /**
   * Fetches the course structure for the current course.
   * @returns {Promise} - Returns a promise that resolves to the course structure
   */
  @action getCourseStructure = async (): Promise<CourseStructureItem[]> => {
    const structure = await getCourseStructure(this.courseSlug, this.language);
    this.courseStructure = structure || this.courseStructure;

    return this.courseStructure;
  };

  /**
   * Fetches the page content for the current course.
   * requires courseSlug and pageSlug to be set before calling this method
   * @returns {Promise} - Returns a promise that resolves to the page content
   */
  @action getCoursePage = async () => {
    if (this.courseSlug && this.pageSlug) {
      try {
        this.isLoadingPage = true;

        const page = await getCoursePage(
          this.courseSlug,
          this.pageSlug,
          this.language,
        );

        if (!page) {
          this.isLoadingPage = false;
          return {};
        }

        this.page = page;
        this.loadedPage = true;
      } catch (fetchPageError) {
        console.error(fetchPageError);
        this.pageFetchError = true;
      } finally {
        this.isLoadingPage = false;
      }
    }
    return this.page;
  };

  /**
   * Changes the page content for the current course, changing the URL accordingly. It also saves the user's progress in that page.
   * @param {string} pageId - The page slug to navigate to
   */
  @action changePageContent = async (pageId: string) => {
    if (!pageId) {
      this.isLoadingPage = false;
      return;
    }
    this.pageSlug = pageId;
    this.currentlabGradeStatus = undefined;

    if (this.routerStore?.route?.params?.page !== this.pageSlug) {
      const { previousRoute } = this.routerStore;

      if (allowedPreviousRoutes.includes(previousRoute?.name)) {
        this.originRouteName = previousRoute?.name;
      }

      this.routerStore?.navigate(
        'courses:page',
        {
          course: this.courseSlug,
          page: this.pageSlug,
        },
        {
          replace: true,
        },
      );
    }

    const coursePage = await this.getCoursePage(); // updates this.page property

    if (this.canShowLabGradeInfo) {
      try {
        const currentlabGradeStatus =
          await getLabGradeStatusByCourseSlugAndPageSlug(
            this.courseSlug,
            this.pageSlug,
          );

        this.currentlabGradeStatus = currentlabGradeStatus;
      } catch (error) {
        this.currentlabGradeStatus = undefined;
        console.error('Failed getting lab grade status', error);
      }
    }

    if (!coursePage?.uuid) return;

    this.userProgressPage = null;
    if (this.pageUUID) {
      this.userProgressPage = await this.progressStore.getUserProgressPage(
        this.pageUUID,
        this.courseSlug,
        this.userStore.username,
      );
    }

    // allSettled always resolves, even if some promises reject
    const resolution: any = await Promise.allSettled([
      this.saveEnrollmentUserData(),
      this.progressStore.setCourseProgress(this.courseSlug, coursePage.uuid),
    ]);

    if (resolution[0]?.status === 'rejected') {
      console.error('Failed saving enrollment user data', resolution[0].reason);
    }

    if (resolution[1]?.status === 'rejected') {
      console.error(
        'Failed saving and getting page progress',
        resolution[1].reason,
      );
    } else if (
      resolution[1]?.status === 'fulfilled' &&
      resolution[1].value?.rv
    ) {
      // Save progress locally
      this.courseProgress = resolution[1].value.rv;
      this.courseProgressLoaded = true;
    }

    if (this.userStore.isXS001Subscriber) {
      await this.saveProgressForSkillsBuild();
    }
  };

  /**
   * Sends an event to the backend indicating that the user has entered the course.
   */
  @action sendCourseJoinEvent = async () => {
    const event = await postEvent('offering_enter', {
      data: {
        offering_slug: this.courseSlug,
        enrollment_uuid: this.euuid,
      },
    });

    return event;
  };

  /**
   * Saves the user's enrollment data for the current course. It saves the user's course settings.
   */
  @action saveEnrollmentUserData = async (settings?: any) => {
    if (this.euuid) {
      const userPreferences: Record<string, boolean> = {};

      if (settings) {
        this.offeringSettings = settings;

        // since only a single setting is being stored
        // as soon as more settings are needed to be stored
        // will change this to validating via the arguments
        if (Object.keys(settings).includes('show_survey50')) {
          userPreferences.show_survey50 = settings.show_survey50;
        }
      }

      let { enrollment } = this;
      if (!enrollment) {
        enrollment = await getEnrollmentInstance(this.euuid);
      }

      let { user_data: oldUserData } = enrollment || {};

      if (!oldUserData) {
        oldUserData = {};
      }

      // I suspect that these settings are not been used anymore and we can remove
      // Now all username@offering setting should be saved on userEnrollmentInfo document
      const newUserData = merge(oldUserData, {
        settings: this.offeringSettings,
      });

      const newEnrollment = await saveEnrollmentUserData(
        newUserData,
        this.euuid,
      );

      const newUserEnrollmentInfo = merge<UserEnrollmentInfo>(
        this.userEnrollmentInfo,
        {
          last_page_accessed: this.pageSlug,
          ...userPreferences,
        },
      );

      saveUserEnrollmentInfo(
        newUserEnrollmentInfo,
        this.userStore.username,
        this.courseSlug,
      );

      this.userEnrollmentInfo = newUserEnrollmentInfo;

      return newEnrollment;
    }
    return null;
  };

  @action resetInitialData = () => {
    this.courseSlug = undefined;
    this.dynamicCourseInfo = undefined;
    this.courseInfoMeta = undefined;
    this.courseOffering = undefined;
    this.courseCode = undefined;
    this.courseVersion = undefined;
    this.courseCatalogEntry = undefined;
    this.translations = [];
    this.versions = [];

    this.euuid = undefined;
    this.enrollment = undefined;
    this.userEnrollmentInfo = undefined;
  };

  @action resetComplemetaryData = () => {
    this.courseStructure = undefined;
    this.pageSlug = undefined;
    this.page = undefined;
  };

  /**
   * Fetches course info meta data.
   * Info meta data contains expert extras, lab plus, page videos and more.
   */
  @action getCourseInfoMeta = async () => {
    try {
      const courseInfoMeta = await getCourseInfoMeta(this.courseSlug);
      this.courseInfoMeta = courseInfoMeta;
    } catch (e) {
      this.courseInfoMeta = null;
      console.error('Failed getting course info meta', e);
    }
  };

  @action getCourseMetadata = async () => {
    let courseMetadata;
    try {
      courseMetadata = await getCourseMetadata(this.courseSlug);
      this.courseMetadata = courseMetadata;
    } catch (error) {
      console.error('Failed to get course metadata:', error);
      return null;
    }
    return courseMetadata;
  };

  /**
   * Main startup method for the course store.
   * This method is called when the course view is loaded.
   * It fetches all the necessary data for the course, except the data that is language dependent.
   * @param {string} slug course slug from url
   */
  @action getInitialData = async (slug: string) => {
    // This method depends on two things: A valid slug AND a loaded catalog in memory
    try {
      this.isLoadingInitialData = true;
      this.courseSlug = slug;

      const [courseInfo, courseOffering] = await Promise.all([
        getDynamicCourseInfo(this.courseSlug),
        getCourseOffering(this.courseSlug),
      ]);
      this.dynamicCourseInfo = courseInfo;
      this.courseOffering = courseOffering;

      const { dynamic_info: dynamicInfo } = this.dynamicCourseInfo || {};
      this.courseCode = dynamicInfo?.code;
      this.courseVersion = dynamicInfo?.current;
      this.translations = dynamicInfo?.variants?.[dynamicInfo?.current] || [];
      this.versions = Object.keys(dynamicInfo?.variants || []);

      this.uiStore.reportActiveCourseTranslations(this.translations);

      this.courseCatalogEntry = this.catalogStore?.getCatalogEntryForCourse(
        this.courseCode,
        this.courseVersion,
      );

      if (!this.courseCatalogEntry) {
        throw new Error(`Course not found in catalog. courseSlug: ${slug}`);
      }

      if (!CourseStore.isBundle(this.courseCatalogEntry)) {
        this.localLabStore.getLabDefinition(this.courseSlug);

        this.getCourseInfoMeta(); // failure is not critical

        const courseEnrollment = await getCourseEnrollment(
          this.courseSlug,
          this.userStore.username,
        );

        const enrollment = await getEnrollmentInstance(courseEnrollment?.uuid);
        this.enrollment = enrollment || {};
        this.euuid = courseEnrollment?.uuid || this.euuid;

        this.userEnrollmentInfo = await getUserEnrollmentInfo(
          this.userStore.username,
          this.courseSlug,
        );

        this.classesStore.getClassesForCurrentUser();
        this.sendCourseJoinEvent();
      }

      if (
        [CourseType.Lesson, CourseType.Regular].includes(
          this.courseCatalogEntry?.course_type,
        )
      ) {
        this.getCourseMetadata();
      }

      await this.getCoursePage();
      if (this.pageUUID) {
        this.userProgressPage = await this.progressStore.getUserProgressPage(
          this.pageUUID,
          this.courseSlug,
          this.userStore.username,
        );
      }

      this.loadedInitialData = true;
      this.initialLoadError = false;
      this.initialErrorCount = 0;
    } catch (e) {
      this.initialErrorCount += 1;
      console.error('Failed getting initial data for the course', e);
      reportError(
        e,
        {
          slug,
          loadedDynamicCourseInfo: !!this.dynamicCourseInfo,
          loadedCourseInfoMeta: !!this.courseInfoMeta,
          loadedCourseOffering: !!this.courseOffering,
          loadedCatalogEntry: !!this.courseCatalogEntry,
        },
        'CourseViewV2.store.getInitialData',
        'error',
      );
      this.initialLoadError = true;
      this.resetInitialData();
    } finally {
      this.isLoadingInitialData = false;
    }
  };

  /**
   * Fetches the complementary data for the course based on the user's language.
   * This includes the course structure, the page content, etc
   */
  @action getComplementaryDataByLanguage = async () => {
    if (
      this.loadedInitialData &&
      this.courseSlug &&
      this.language &&
      !this.isBundledCourse
    ) {
      try {
        this.isLoadingComplementaryData = true;
        const structure = await this.getCourseStructure(); // used for ToC, progress and page navigation

        const pageURLParam = this.routerStore?.route?.params?.page;
        // prioritize para from url param over last bookmarked page
        const pageToLoad =
          pageURLParam ||
          CourseStore.getLastPageAccessed(this.userEnrollmentInfo, structure);

        this.changePageContent(pageToLoad);

        this.complementaryDataLoadError = false;
        this.loadedComplementaryData = true;
      } catch (e) {
        reportError(
          e,
          {
            slug: this.courseSlug,
            language: this.language,
            loadedCourseStructure: !!this.courseStructure,
          },
          'CourseViewV2.store.getComplementaryDataByLanguage',
          'error',
        );
        console.error('Failed getting complementary data for the course', e);
        this.complementaryDataLoadError = true;
      } finally {
        this.isLoadingComplementaryData = false;
      }
    }
  };

  /**
   * Saves progress for specifically for SkillsBuild courses
   */
  @action saveProgressForSkillsBuild = async () => {
    const courseProgress = this.progressStore.getProgressForCourse(
      this.courseSlug,
    );

    if (
      courseProgress &&
      (this.progressStore.didJustEnrolledInCourse(courseProgress) ||
        this.progressStore.didJustFinishedCourse(courseProgress))
    ) {
      try {
        this.progressStore.setCourseProgressExternalSubscription(
          this.courseCode,
          courseProgress,
          this.userStore.subscription.subscription,
        );
      } catch (error) {
        console.error(error);
      }
    }
  };

  @action handleCurrentPageAsBookmarked = async () => {
    if (this.isCurrentPageBookmarked) {
      // remove
      await this.handleBookmark(this.pageSlug, undefined);
    } else {
      // add
      await this.handleBookmark(this.pageSlug, this.pageTitle);
    }
  };

  /**
   * Handles the bookmarking of a section in the course.
   * It will create a new bookmark if it doesn't exist, or remove it if it does.
   * @param {object} section
   * @param {string} title
   */
  @action handleBookmark = async (section: string, title: string) => {
    if (!section && !title) {
      return {};
    }
    try {
      const courseOffering = await createUpdateCourseBookmark(
        this.courseSlug,
        section,
        title,
      );

      this.courseOffering = courseOffering;
    } finally {
      // eslint-disable-next-line no-unsafe-finally
      return this.courseOffering;
    }
  };

  /**
   * Opens the feedback drawer for the current course.
   * It will send the course and page info to the feedback form.
   */
  @action openFeedbackDrawer = () => {
    const data = {
      course: this.courseCode,
      courseVersion: this.courseVersion,
      pageTitle: this.pageTitle,
      uuid: this.page?.uuid,
      url: (window as any).location.href,
    };
    this.eventStore.toggleFeedbackForm(data);
  };

  @computed get enrollmentCore() {
    return this.enrollment?.core || {};
  }

  @computed get lastWatchedTimestampInPage() {
    if (!this.userProgressPage) {
      return undefined;
    }
    return this.userProgressPage?.last_watched_timestamp;
  }

  /**
   * Computes the course_info for the most recent version of the course
   */
  @computed get currentCourse(): CourseHeaderSummary {
    return CourseStore.getCurrentCourse(this.dynamicCourseInfo, this.language);
  }

  @computed get previousPageTag() {
    if (this.page?.prev_tag) {
      return this.page.prev_tag;
    }

    if (!this.courseStructure) {
      return null;
    }

    // if there is no prev_tag in the page, we need to find the previous page in the course structure
    const index = this.courseStructure.findIndex(
      (s) => s.page_tag === this.pageSlug,
    );
    return index > 0 ? this.courseStructure[index - 1].page_tag : null;
  }

  @computed get title(): string {
    return this.currentCourse?.title;
  }

  @computed get enrollmentNotFound() {
    return this.loadedInitialData && !this.euuid;
  }

  @computed get userData() {
    return this.enrollment?.user_data;
  }

  @computed get expertExtras() {
    return this.courseInfoMeta?.expert_extras;
  }

  @computed get hasExpertExtrasInfo() {
    return (
      this.courseInfoMeta?.doc_id === this.courseSlug &&
      this.expertExtras?.kaltura_playlists.length > 0
    );
  }

  @computed get modality(): Modality {
    return this.currentCourse?.modality as Modality;
  }

  @computed get isTrialCourse() {
    return this.courseCatalogEntry?.tier === OfferingTier.RHLSTrial;
  }

  @computed get courseType() {
    return this.courseCatalogEntry?.course_type;
  }

  @computed get isOpenSubscriberForCourse() {
    return this.userStore.isOpenSubscriberForCourse(this.courseCode);
  }

  @computed get isCourse() {
    return this.modality === Modality.Course;
  }

  @computed get isBundledCourse() {
    return CourseStore.isBundle(this.courseCatalogEntry);
  }

  @computed get isLesson() {
    return CatalogStore.isLesson(this.courseType);
  }

  @computed get isLabPlus() {
    return CatalogStore.isLabPlus(this.courseType);
  }

  @computed get isTechOverview() {
    return CatalogStore.isTechOverview(this.modality);
  }

  @computed get labPlusCourses() {
    return this.courseInfoMeta?.lab_plus;
  }

  @computed get hasLabPlusCourseInfo() {
    return (
      this.courseInfoMeta?.doc_id === this.courseSlug &&
      this.labPlusCourses?.code.length > 0
    );
  }

  @computed get isEarlyAccess() {
    const { dynamic_info: dynamicInfo } = this.dynamicCourseInfo || {};
    return dynamicInfo?.summaries[0]?.status === 'early';
  }

  @computed get courseTotalProgress() {
    if (!this.courseProgress?.total_progress) {
      return null;
    }
    return Math.floor(this.courseProgress.total_progress * 100);
  }

  @computed get courseCompletionTime() {
    return this.courseProgress?.completion_time;
  }

  @computed get completionTime() {
    return this.enrollment?.completion_time;
  }

  @computed get epochVersions() {
    const { dynamic_info: dynamicInfo } = this.dynamicCourseInfo || {};
    return dynamicInfo?.epoch_version_number || {};
  }

  @computed get sortedEpochVersions() {
    return Object.keys(this.epochVersions)
      .map((version) => [version, this.epochVersions[version]])
      .sort((a, b) => a[1] - b[1])
      .map((e) => e[0]);
  }

  @computed get sortedVersions() {
    // filter to exclude epoch versions that are in the product versions list and sort
    const sortedVersions = this.versions
      ?.filter((version) => !this.sortedEpochVersions?.includes(version))
      .map((a) =>
        a
          .split('.')
          .map((n) => +n + 100000)
          .join('.'),
      )
      .sort()
      .map((a) =>
        a
          .split('.')
          .map((n) => +n - 100000)
          .join('.'),
      );

    return sortedVersions?.concat?.(this.sortedEpochVersions) || [];
  }

  @computed get sortedVersionDesc() {
    return reverse([...this.sortedVersions]);
  }

  @computed get latestVersion() {
    if (!this.sortedVersions.length) {
      return null;
    }
    return this.sortedVersions.slice(-1)[0];
  }

  @computed get isLatestVersion() {
    return (
      this.latestVersion && this.currentCourse?.version === this.latestVersion
    );
  }

  @computed get progressStructure() {
    return this.courseProgress?.structure || [];
  }

  static isPageBlockedForFreeTier(pageTag) {
    return !/pr01|ch01|ch02/.test(pageTag);
  }

  @computed get isCurrentPageBlockedForFreeTier() {
    return (
      !this.isLoadingPage &&
      this.userStore.isFreeTierSubscriber &&
      CourseStore.isPageBlockedForFreeTier(this.pageSlug)
    );
  }

  isPageBlockedForFreeTierSubscriber = (pageTag: string) =>
    this.userStore.isFreeTierSubscriber &&
    CourseStore.isPageBlockedForFreeTier(pageTag);

  @computed get courseSections() {
    return (
      this.courseStructure?.filter((item) => item.type === 'section') || []
    );
  }

  @computed get currentPageNumber() {
    return (
      (this.courseSections?.findIndex(
        (item) => item.page_tag === this.pageSlug,
      ) || 0) + 1
    );
  }

  @computed get totalPageNumber() {
    return this.courseSections?.length || 0;
  }

  @computed get currentSection() {
    return this.courseSections?.find((item) => item.page_tag === this.pageSlug);
  }

  @computed get firstSection() {
    return this.courseSections?.[0];
  }

  @computed get currentChapter() {
    const chapterId = this.currentSection?.parent_id;

    if (!chapterId) {
      return null;
    }

    return this.courseStructure?.find((item) => item.uuid === chapterId);
  }

  @computed get isNextButtonDisabled() {
    const nextPageTag = this.page?.next_tag;
    const isLastPage = this.currentPageNumber === this.totalPageNumber;
    return (
      this.isLoadingInitialData ||
      isLastPage ||
      !nextPageTag ||
      this.isPageBlockedForFreeTierSubscriber(nextPageTag)
    );
  }

  @action goToNextPage = async () => {
    if (this.isNextButtonDisabled) {
      return;
    }
    const nextPageTag = this.page?.next_tag;
    if (nextPageTag) {
      this.pageSlug = nextPageTag;

      this.routerStore?.navigate('courses:page', {
        course: this.courseSlug,
        page: this.pageSlug,
      });
    }
  };

  @computed get isPrevButtonDisabled() {
    const prevPageTag = this.previousPageTag;
    const isFirstPage = this.currentPageNumber === 1 && !prevPageTag;
    return (
      this.isLoadingInitialData ||
      isFirstPage ||
      !prevPageTag ||
      this.isPageBlockedForFreeTierSubscriber(prevPageTag)
    );
  }

  @action goToPrevPage = async () => {
    if (this.isPrevButtonDisabled) {
      return;
    }
    const prevPageTag = this.previousPageTag;
    if (prevPageTag) {
      this.pageSlug = prevPageTag;

      this.routerStore?.navigate('courses:page', {
        course: this.courseSlug,
        page: this.pageSlug,
      });
    }
  };

  @computed get content() {
    return this.page?.content?.html || '';
  }

  @computed get buildEnv() {
    return this.page?.build_env || {};
  }

  @computed get pageUUID() {
    return this.page?.uuid || undefined;
  }

  @computed get bookmarkList() {
    let bookmarkArray = [];

    if (this.courseOffering && this.courseOffering.bookmarks) {
      const { bookmarks } = this.courseOffering;
      bookmarkArray = bookmarks.filter(
        (item, index) => bookmarks.indexOf(item) === index,
      );
    }

    return bookmarkArray;
  }

  @computed get isCurrentPageBookmarked() {
    return this.bookmarkList?.some(
      (bookmark) => bookmark.section === this.pageSlug,
    );
  }

  @computed get pageWithVideo() {
    const videosForCurrentPage = this.courseInfoMeta?.page_videos?.find(
      (v) => v.page_slug === this.pageSlug,
    )?.entries;

    const defaultLanguage = 'en-US';
    const currentLanguage = this.language || defaultLanguage;

    let videosForCurrentPageByLanguage =
      videosForCurrentPage?.length > 0 &&
      videosForCurrentPage?.filter((v) => v.language === currentLanguage);
    videosForCurrentPageByLanguage =
      videosForCurrentPageByLanguage?.length > 0
        ? videosForCurrentPageByLanguage
        : videosForCurrentPage?.filter((v) => v.language === defaultLanguage);

    return videosForCurrentPage?.length > 0
      ? (videosForCurrentPageByLanguage.length > 0 &&
          videosForCurrentPageByLanguage[0]) ||
          videosForCurrentPage[0]
      : null;
  }

  @computed
  get pageInteractiveDemo(): string | undefined {
    const id = this.courseInfoMeta?.page_interactive_demos?.[this.pageSlug];

    if (!id) {
      return undefined;
    }

    const params = {
      embed: true,
      embed_mobile: 'inline',
      embed_desktop: 'inline',
      show_copy_link: false,
    };
    const queryParams = queryString.stringify(params);
    const url = `${this.configStore.arcadeSoftwareDemoUrl}/${id}${
      queryParams ? `?${queryParams}` : ''
    }`;

    return url;
  }

  @computed get playListId() {
    if (!this.expertExtras) {
      return null;
    }

    const playlists = this.expertExtras.kaltura_playlists;
    const result = playlists.filter((obj) => obj.playlist_name === 'main');
    return result[0].id;
  }

  // UI related checks:
  @computed get videoWidgetTitle() {
    const truncate = (anyTitle: string, maxLength: number) =>
      anyTitle?.length > maxLength
        ? `${anyTitle?.slice(0, maxLength - 1)} ...`
        : anyTitle;
    const truncatedCourseTitle = truncate(this.title, 70);
    const truncatedSectionTitle = truncate(this.pageTitle, 70);

    return ` - ${truncatedCourseTitle} | ${truncatedSectionTitle}`;
  }

  @computed get canShowToolbar() {
    return !this.userStore.isFreeTierSubscriber;
  }

  @computed get canShowToolbarMenu() {
    return (
      this.loadedInitialData &&
      !this.isOpenSubscriberForCourse &&
      !this.userStore.isLS101Subscriber &&
      !this.userStore.isExternalSubscriber &&
      !this.userStore.isFreeTierSubscriber
    );
  }

  @computed get canShowExpertExtras() {
    return this.hasExpertExtrasInfo && !this.isOpenSubscriberForCourse;
  }

  @computed get canShowLabPlus() {
    return (
      this.hasLabPlusCourseInfo && this.catalogStore.subscriptionHasLabPlus
    );
  }

  @computed get canShowBundledCourseToolbar() {
    const showToolbar =
      this.loadedInitialData &&
      !this.userStore.isFreeTierSubscriber &&
      !this.isOpenSubscriberForCourse &&
      !this.userStore.isLS101Subscriber &&
      !this.userStore.isExternalSubscriber;

    return showToolbar;
  }

  @computed get showSurvey() {
    const preference = this.userEnrollmentInfo?.show_survey50;

    if (preference !== undefined && preference !== null) {
      return preference;
    }

    return true;
  }

  @computed get canShowAutostart() {
    const preference = this.enrollment?.user_data?.settings?.show_autostart;
    if (preference !== undefined && preference !== null) {
      return preference;
    }
    return true;
  }

  @computed get is50reached() {
    return Number(this.courseTotalProgress) >= 50;
  }

  @computed get canShowSurveyDialog() {
    return (
      !this.userStore.isOpenSubscriber &&
      this.showSurvey &&
      this.is50reached &&
      !this.isLesson &&
      !this.isTrialCourse
    );
  }

  @computed get isEbookEnabled() {
    return !this.isLesson;
  }

  @computed get canShowBookshelf() {
    return !(this.isLabPlus || this.isEarlyAccess);
  }

  @computed get bookshelfURL() {
    return `${this.configStore.ebooksPlatformURL}ebook/${this.courseSlug}`;
  }

  @computed get isFreeToolbar() {
    return (
      this.isOpenSubscriberForCourse ||
      this.userStore.isFreeTierSubscriber ||
      this.userStore.isLS101Subscriber ||
      this.userStore.isExternalSubscriber
    );
  }

  @computed get canShowVideoPlayer() {
    return (
      !this.userStore.isOpenSubscriber &&
      !this.isCurrentPageBlockedForFreeTier &&
      this.pageWithVideo
    );
  }

  @computed get customVideoPlayerId() {
    return this.courseInfoMeta?.custom_player_id;
  }

  @computed get canShowCertAttendanceButton() {
    return (
      !this.userStore.isOpenSubscriber &&
      !this.isLesson &&
      this.progressStore.shouldShowLegacyCertificateOfAttendance(
        Number(this.courseTotalProgress),
        this.courseCompletionTime,
      )
    );
  }

  @computed get legacyAttendanceTimestamp() {
    if (this.canShowCertAttendanceButton) {
      return dateToTimestamp(new Date());
    }

    return this.completionTime;
  }

  @computed get pageTitle() {
    return this.page?.title || '';
  }

  @computed get canShowOverview() {
    return this.isLesson || this.isCourse || this.isTechOverview;
  }

  @computed get canShowLabPlusTab() {
    return (
      this.hasLabPlusCourseInfo && this.catalogStore.subscriptionHasLabPlus
    );
  }

  @computed get canShowCourseCompletionNotification() {
    return !this.canShowCertAttendanceButton && !this.isLesson;
  }

  @computed get originRoute() {
    const routeLabels = this.routerStore?.router?.config?.defaultParams;

    const definition = {
      to: this.originRouteName,
      label: routeLabels?.[this.originRouteName]?.title,
    };

    return definition;
  }

  @computed get canShowLabGradeInfo() {
    const isLabNotificationEnabled = this.featuresStore.isFeatureEnabled(
      'enablements',
      EnablementFeatureFlags.LabNotificationEnabled,
      false,
    );

    if (!isLabNotificationEnabled || !this.page?.tasks) {
      return false;
    }

    return (
      (this.page?.tasks?.lab?.[0].mode === 'lab' &&
        this.page?.tasks?.lab?.[0].type === 'lab') ||
      (this.page?.tasks?.exercise?.[0].type === 'review' &&
        this.page?.tasks?.exercise?.[0].mode === 'exercise')
    );
  }
}

export default CourseStore;

export const courseContext = createContext(null);
